// Hook viewer de streaming en directo (lado terapeuta).
//
// Responsabilidades:
//   1. GET TURN credentials al server (Cloudflare TURN o fallback STUN).
//   2. Abrir el WS al endpoint generico de signaling con `?token=<JWT>`.
//   3. Identificarse como `hello-viewer`. Esperar `welcome` y `peer-ready`.
//   4. Crear RTCPeerConnection cuando llegue peer-ready. Recibir offer del
//      game, generar answer, intercambiar ICE candidates.
//   5. Pegar el primer track recibido al elemento <video>.
//   6. Heartbeat `{type:'ping'}` cada 20s (sin esto Cloud Run cierra a 40s).
//   7. Stats `pc.getStats()` cada 1s para extraer RTT y bitrate.
//   8. Reaccionar a `kill-switch` y `peer-left` del game.
//   9. Cleanup TOTAL al desmontar o cuando enabled pasa a false (ws+pc+
//      intervals+video.srcObject).
//
// Inputs: { accessToken, code, gameKey, adapterSlug, enabled }
//
// Outputs (estado):
//   status: 'idle' | 'requesting-turn' | 'waiting-peer' | 'connecting' |
//           'live' | 'killed-by-fps' | 'peer-left' | 'error' | 'ended'
//   fps: numero | null  (FPS reportado por el game via mensaje 'metrics')
//   rtt: numero | null  (round trip time en ms, de pc.getStats())
//   bitrate: numero | null  (bits/s recibidos del video, de pc.getStats())
//   errorReason: string | null
//   killSwitchReason: string | null
//   videoRef: ref para asignar al elemento <video> del componente

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReviLiveStreamApi } from "../../api/revi/liveStreamApi";

const api = new ReviLiveStreamApi();

const STATUS = {
  IDLE: "idle",
  REQUESTING_TURN: "requesting-turn",
  WAITING_PEER: "waiting-peer",
  CONNECTING: "connecting",
  LIVE: "live",
  KILLED: "killed-by-fps",
  PEER_LEFT: "peer-left",
  ERROR: "error",
  ENDED: "ended",
};

const HEARTBEAT_MS = 20 * 1000;
const STATS_MS = 1000;

export function useLiveStreamViewer({
  accessToken,
  code,
  gameKey,
  adapterSlug = "revi-motricity",
  enabled = true,
}) {
  // Solo se traduce el aviso que lee el terapeuta. Los otros `errorReason` de
  // este hook son diagnostico tecnico de WebRTC ("ICE connection failed",
  // "WebSocket error", "SDP answer: ...") y se quedan como estan a proposito:
  // no son copia de producto y traducirlos estorbaria al soporte.
  const { t } = useTranslation("misc");
  const [status, setStatus] = useState(STATUS.IDLE);
  const [fps, setFps] = useState(null);
  const [rtt, setRtt] = useState(null);
  const [bitrate, setBitrate] = useState(null);
  const [errorReason, setErrorReason] = useState(null);
  const [killSwitchReason, setKillSwitchReason] = useState(null);

  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const heartbeatRef = useRef(null);
  const statsRef = useRef(null);
  const lastStatsRef = useRef({ bytesReceived: 0, t: Date.now() });
  const cleanedRef = useRef(false);
  const iceServersRef = useRef(null);

  // Cleanup completo. Idempotente y seguro de llamar varias veces.
  const cleanup = useCallback((nextStatus) => {
    if (cleanedRef.current && nextStatus === undefined) return;
    cleanedRef.current = true;
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (statsRef.current) {
      clearInterval(statsRef.current);
      statsRef.current = null;
    }
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (_e) {
        /* noop */
      }
      pcRef.current = null;
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "bye" }));
        }
        wsRef.current.close();
      } catch (_e) {
        /* noop */
      }
      wsRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch (_e) {
        /* noop */
      }
    }
    if (nextStatus) setStatus(nextStatus);
  }, []);

  // Helper de envio JSON al WS.
  const wsSend = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(payload));
    } catch (_e) {
      /* noop */
    }
  }, []);

  // Crea la PeerConnection cuando llegue peer-ready o cuando el game
  // ya este conectado (game manda offer => si pc no existe, la creamos).
  const ensurePeerConnection = useCallback(() => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({
      iceServers: iceServersRef.current || [],
    });

    pc.addEventListener("track", (ev) => {
      if (!videoRef.current) return;
      // ev.streams[0] puede ser undefined si el peer remoto llamo
      // pc.addTrack(track) SIN segundo argumento (caso del modulo del
      // game v1.0.x). En ese caso ev.streams es array vacio y srcObject
      // nunca se asignaba -> video negro pese a recibir bytes. Fallback:
      // creamos un MediaStream local con el track recibido.
      const stream =
        ev.streams && ev.streams[0]
          ? ev.streams[0]
          : new MediaStream([ev.track]);
      console.info(
        `[live-stream-viewer] track recibido (kind=${ev.track.kind}, viaStream=${!!(ev.streams && ev.streams[0])})`,
      );
      videoRef.current.srcObject = stream;
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          /* autoplay block silencioso; el user gesture lo arregla */
        });
      }
    });

    pc.addEventListener("icecandidate", (ev) => {
      if (ev.candidate) {
        wsSend({
          type: "signal",
          payload: { kind: "candidate", candidate: ev.candidate.toJSON() },
        });
      }
    });

    pc.addEventListener("iceconnectionstatechange", () => {
      const s = pc.iceConnectionState;
      if (s === "connected" || s === "completed") {
        setStatus((cur) =>
          cur === STATUS.KILLED || cur === STATUS.ERROR ? cur : STATUS.LIVE,
        );
      } else if (s === "failed") {
        setStatus(STATUS.ERROR);
        setErrorReason("ICE connection failed");
      } else if (s === "disconnected") {
        setStatus((cur) => (cur === STATUS.LIVE ? STATUS.PEER_LEFT : cur));
      }
    });

    // Stats loop: extrae bitrate de inbound video y rtt del candidate-pair.
    statsRef.current = setInterval(async () => {
      if (!pcRef.current) return;
      try {
        const report = await pcRef.current.getStats();
        let inboundBytes = 0;
        let currentRtt = null;
        report.forEach((s) => {
          if (s.type === "inbound-rtp" && s.kind === "video") {
            inboundBytes = s.bytesReceived || 0;
          }
          if (
            s.type === "candidate-pair" &&
            s.state === "succeeded" &&
            typeof s.currentRoundTripTime === "number"
          ) {
            currentRtt = Math.round(s.currentRoundTripTime * 1000);
          }
        });
        const now = Date.now();
        const dt = (now - lastStatsRef.current.t) / 1000;
        const dBytes = inboundBytes - lastStatsRef.current.bytesReceived;
        if (dt > 0 && dBytes >= 0 && lastStatsRef.current.bytesReceived > 0) {
          setBitrate(Math.round((dBytes * 8) / dt));
        }
        lastStatsRef.current = { bytesReceived: inboundBytes, t: now };
        if (currentRtt !== null) setRtt(currentRtt);
      } catch (_e) {
        /* noop */
      }
    }, STATS_MS);

    pcRef.current = pc;
    return pc;
  }, [wsSend]);

  // Handler central del WS: procesa los mensajes JSON del server.
  const handleMessage = useCallback(
    async (raw) => {
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch (_e) {
        return;
      }
      switch (msg.type) {
        case "welcome":
          console.info("[live-stream-viewer] welcome");
          setStatus(STATUS.WAITING_PEER);
          break;
        case "peer-ready":
          if (msg.role === "game") {
            console.info("[live-stream-viewer] peer-ready (game)");
            setStatus(STATUS.CONNECTING);
            ensurePeerConnection();
          }
          break;
        case "peer-left":
          if (msg.role === "game") {
            console.info("[live-stream-viewer] peer-left (game)");
            setStatus(STATUS.PEER_LEFT);
          }
          break;
        case "signal": {
          const pc = ensurePeerConnection();
          const payload = msg.payload || {};
          if (payload.kind === "offer" && payload.sdp) {
            console.info("[live-stream-viewer] offer recibida, aplicando");
            try {
              await pc.setRemoteDescription({
                type: "offer",
                sdp: payload.sdp,
              });
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              wsSend({
                type: "signal",
                payload: { kind: "answer", sdp: answer.sdp },
              });
              console.info("[live-stream-viewer] answer enviada");
            } catch (e) {
              console.error("[live-stream-viewer] SDP answer error", e);
              setStatus(STATUS.ERROR);
              setErrorReason(t("liveStream.sdpError", { detalle: e.message }));
            }
          } else if (payload.kind === "candidate" && payload.candidate) {
            try {
              await pc.addIceCandidate(payload.candidate);
            } catch (_e) {
              /* ICE candidate puede llegar antes de remote desc */
            }
          }
          break;
        }
        case "metrics":
          if (typeof msg.fps === "number") setFps(msg.fps);
          break;
        case "kill-switch":
          setStatus(STATUS.KILLED);
          setKillSwitchReason(msg.reason || "fps-degraded");
          if (typeof msg.fps === "number") setFps(msg.fps);
          break;
        case "pong":
          break;
        default:
          break;
      }
    },
    [ensurePeerConnection, wsSend, t],
  );

  // Effect principal: arranca el pipeline cuando hay token+code+enabled.
  useEffect(() => {
    if (!enabled || !accessToken || !code) {
      cleanup(STATUS.IDLE);
      cleanedRef.current = false;
      return undefined;
    }

    let cancelled = false;
    cleanedRef.current = false;
    setStatus(STATUS.REQUESTING_TURN);
    setErrorReason(null);
    setKillSwitchReason(null);

    (async () => {
      try {
        const turn = await api.getTurnCredentials(accessToken);
        if (cancelled) return;
        iceServersRef.current = turn.iceServers || [];

        const wsUrl = api.buildWsUrl({
          code,
          token: accessToken,
          adapterSlug,
        });
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          wsSend({ type: "hello-viewer" });
          heartbeatRef.current = setInterval(() => {
            wsSend({ type: "ping" });
          }, HEARTBEAT_MS);
        };
        ws.onmessage = (ev) => {
          handleMessage(ev.data);
        };
        ws.onerror = () => {
          if (cancelled) return;
          setStatus(STATUS.ERROR);
          setErrorReason("WebSocket error");
        };
        ws.onclose = (ev) => {
          if (cancelled) return;
          // Codigos 4xxx son rechazos del adapter. 1000 es bye normal.
          if (ev.code >= 4000 && ev.code <= 4999) {
            setStatus(STATUS.ERROR);
            setErrorReason(
              ev.reason || t("liveStream.closedByServer", { codigo: ev.code }),
            );
          } else if (ev.code !== 1000) {
            setStatus((cur) =>
              cur === STATUS.KILLED ||
              cur === STATUS.ERROR ||
              cur === STATUS.ENDED
                ? cur
                : STATUS.ENDED,
            );
          }
        };
      } catch (e) {
        if (cancelled) return;
        setStatus(STATUS.ERROR);
        setErrorReason(e.message || t("liveStream.startError"));
      }
    })();

    return () => {
      cancelled = true;
      cleanup(STATUS.ENDED);
    };
    // gameKey no participa en el ciclo: solo es informativo
    // (la validacion la hace el server con el code y el JWT del therapist).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, code, adapterSlug, enabled]);

  return {
    status,
    fps,
    rtt,
    bitrate,
    errorReason,
    killSwitchReason,
    videoRef,
    gameKey,
  };
}

useLiveStreamViewer.STATUS = STATUS;
